import React, { Fragment, useState } from "react"

import { useTranslation } from "react-i18next"
import { useMutation } from "react-query"
import { useSelector } from "react-redux"

import { NotificationUtils } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { BaseSimpleDialog } from "../../components/dialogs/BaseSimpleDialog"
import { SidAPI } from "../../services/SidAPI"
import { queryClient } from "../../store/QueryClientStore"
import type { RootState } from "../../store/ReduxStore"
import type { PatchedGroupRequest } from "../../types/custom/Permissions"
import type { GroupRetrieve } from "../../types/generated/authentication/GroupRetrieve"
import type { KonanResource } from "../../types/generated/authentication/KonanResource"
import { useIsPermitted } from "../../utils/PermissionsHelpers"
import type { MenuOption } from "../Members"
import { RoleCard } from "../Roles/RoleCard"
import { GroupCreationDialog } from "./GroupCreationDialog"
import type { GroupContainerInterfaceProps } from "./Interfaces"

/**
 * Group container holding all the group logic
 * @param {GroupRetrieve} group
 * @param {boolean} immutable (optional)
 * @param {string[]} users (optional)
 * @returns {React.ReactElement}
 */
export function GroupContainer(props: Readonly<GroupContainerInterfaceProps>): React.ReactElement {
  const { group, immutable = true, users } = props

  const permissions = useSelector((state: RootState) => state.auth.permissions)
  const organizationUUID = useSelector((state: RootState) => state.auth.organizationId) as string

  const flattenedSidPermissions = useSelector((state: RootState) => state.permissions.flattenedSidPermissions)

  const { t } = useTranslation("settings", {
    keyPrefix: "accessControl.sections.groups.container",
  })

  const [isDeletionDialogOpen, setIsDeletionDialogOpen] = useState<boolean>(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false)
  const [isViewMode, setIsViewMode] = useState<boolean>(false)

  const deleteGroupMutation = useMutation<AxiosResponse, AxiosError, string>(SidAPI.deleteGroup, {
    onSuccess: async () => {
      NotificationUtils.toast(t("notifications.deleteGroup.success"), {
        severity: "success",
      })

      await queryClient.invalidateQueries("groups")
      await queryClient.invalidateQueries("change-history")
    },
    onError: () =>
      NotificationUtils.toast(t("notifications.deleteGroup.error"), {
        severity: "error",
      }),
  })

  const editGroupsMutation = useMutation<AxiosResponse<GroupRetrieve>, AxiosError, PatchedGroupRequest>(
    SidAPI.updateGroup,
    {
      onSuccess: () => {
        NotificationUtils.toast(t("notifications.editGroup.success"), {
          severity: "success",
        })

        queryClient.invalidateQueries(["groups", organizationUUID])
        queryClient.invalidateQueries("change-history")
      },
      onError: () =>
        NotificationUtils.toast(t("notifications.editGroup.error"), {
          severity: "error",
        }),
    },
  )

  const updateGroupHandler = async (name: string, konan_resources: KonanResource[]): Promise<void> => {
    await editGroupsMutation.mutateAsync({
      uid: group.uid,
      name: name,

      konan_resources: konan_resources,
    })

    setIsDeletionDialogOpen(false)
  }

  const MenuOptions = (): MenuOption[] => {
    const options: MenuOption[] = []

    useIsPermitted("Update groups", permissions.synapse_id, flattenedSidPermissions) &&
      options.push({
        title: t("menu.edit"),
        variant: "base",
        onClick: () => {
          setIsEditDialogOpen(true)
        },
      })

    useIsPermitted("Delete groups", permissions.synapse_id, flattenedSidPermissions) &&
      options.push({
        title: t("menu.delete"),
        variant: "negative",
        onClick: () => {
          setIsDeletionDialogOpen(true)
        },
      })

    return options
  }

  return (
    <Fragment>
      {/* Dialogs */}
      <GroupCreationDialog
        isOpen={isEditDialogOpen}
        onClose={() => {
          setIsEditDialogOpen(false)
          setTimeout(() => {
            setIsViewMode(false)
          }, 300)
        }}
        group={group}
        onAccept={updateGroupHandler}
        isViewMode={isViewMode}
      />

      {isDeletionDialogOpen && (
        <BaseSimpleDialog
          open={isDeletionDialogOpen}
          isLoading={deleteGroupMutation.isLoading}
          name={group.name}
          onAccept={() => {
            deleteGroupMutation.mutateAsync(group.uid)
            setIsDeletionDialogOpen(false)
          }}
          onClose={() => setIsDeletionDialogOpen(false)}
          mode={"group-deletion"}
        />
      )}

      <RoleCard
        name={group.name}
        onTitleClick={
          !["All Projects"].includes(group.name)
            ? () => {
                setIsViewMode(true)
                setIsEditDialogOpen(true)
              }
            : undefined
        }
        date={group.created_at}
        immutable={immutable}
        users={users}
        MenuOptions={MenuOptions()}
      />
    </Fragment>
  )
}
